import { Button, Flex } from '@mantine/core'
import { clsx } from 'clsx'
import { useMemo } from 'react'

import { type CoreBooking } from '@flecto/types'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import {
  type BookingActionType,
  DropdownBookingActions,
} from '../../../booking-actions'
import { ButtonAction } from './ButtonAction'
import styles from './styles.module.css'

export type HeaderBookingActionsNextProps = {
  booking?: CoreBooking
  bookingProducts?: CoreBooking['booking_products']
}

export function HeaderBookingActionsNext({
  booking,
  bookingProducts,
}: HeaderBookingActionsNextProps) {
  const { isDesktop } = useMediaQuery()
  const bookingId = booking?.internal_id

  const excludeActions = useMemo(() => {
    switch (booking?.status) {
      case 'ongoing':
        return ['pickup', 'return']

      case 'drafting':
        return ['create_quote', 'reserve', 'pickup']

      case 'reserved':
        return ['pickup']

      case 'negotiating':
        return ['reserve', 'pickup']

      case 'canceled':
        return ['restore_cancel']

      case 'completed':
      default:
        return []
    }
  }, [booking]) satisfies BookingActionType[]

  const hasBookingProducts = useMemo(
    () => Boolean(bookingProducts?.length ?? 0),
    [bookingProducts]
  )

  if (!booking) {
    return null
  }

  return (
    <Flex gap='sm'>
      <Button.Group
        className={clsx(styles.buttonGroupActions)}
        style={{ display: isDesktop ? 'block' : 'none' }}
      >
        {booking?.status === 'ongoing' && (
          <>
            <ButtonAction actionKey='pickup' bookingId={bookingId} />
            <ButtonAction actionKey='return' bookingId={bookingId} />
          </>
        )}
        {booking?.status === 'drafting' && (
          <>
            <ButtonAction
              actionKey={'create_quote'}
              bookingId={bookingId}
              disabled={!hasBookingProducts}
            />
            <ButtonAction
              actionKey='reserve'
              bookingId={bookingId}
              disabled={!hasBookingProducts}
            />
            <ButtonAction
              actionKey='pickup'
              bookingId={bookingId}
              disabled={!hasBookingProducts}
            />
          </>
        )}
        {booking?.status === 'negotiating' && (
          <>
            <ButtonAction actionKey='reserve' bookingId={bookingId} />
            <ButtonAction actionKey='pickup' bookingId={bookingId} />
          </>
        )}
        {booking?.status === 'reserved' && (
          <ButtonAction actionKey='pickup' bookingId={bookingId} />
        )}
        {booking?.status === 'canceled' && (
          <ButtonAction actionKey='restore_cancel' bookingId={bookingId} />
        )}
      </Button.Group>
      <DropdownBookingActions
        bookingId={bookingId}
        excludeActions={isDesktop ? excludeActions : []}
      />
    </Flex>
  )
}
